import { Controller } from "stimulus";
import Choices from "choices.js";
import debounce from "lodash.debounce";

export default class extends Controller {
  static targets = ["field"];
  static values = {
    url: String,
    queryName: String,
    addItems: Boolean,
    additionalQueryParams: String,
    labelField: String
  };

  initialize() {
    this.search = debounce(this.search, 500);
    this.currentPage = 1;
    this.isLoading = false;
    this.reachedLastPage = false;
    this.lastQuery = undefined;
  }

  connect(){
    const fieldInput = this.fieldTarget;

    this.choices = new Choices(fieldInput, {
      removeItemButton: true,
      searchPlaceholderValue: 'Type to search',
      noChoicesText: this.addItemsValue ? 'Press Enter to add item' : 'No choices to choose from',
      shouldSort: false,
      searchChoices: false,
      position: 'bottom'
    });
    // Attach to dropdown open in order to prime results and hook scroll
    // Some Choices builds dispatch on passedElement.element (original select)
    const dropdownEventTarget = this.choices.passedElement?.element || this.choices.input.element;
    dropdownEventTarget.addEventListener('showDropdown', () => this.onShowDropdown());
    if (this.addItemsValue) {
      this.choices.input.element.addEventListener('keyup', (event) => {
        if (event.keyCode === 13 && event.target.value) {
          this.addItem(event.target.value);
          event.target.value = '';
        }
      });
    }
  }

  search(event) {
    const query = event && event.detail ? event.detail.value : undefined;
    // Reset pagination when query changes or when invoked without query on open
    this.lastQuery = query;
    this.currentPage = 1;
    this.reachedLastPage = false;
    this.fetchPage({ replace: true });
  }

  addItem(value) {
    const labelField = this.labelFieldValue || 'label';
    this.choices.setChoices([{ value: value, [labelField]: value, selected: true }], 'value', labelField, false);
  }

  onShowDropdown() {
    // If nothing loaded yet for current query, load first page
    if (this.currentPage === 1 && !this.isLoading && !this.reachedLastPage) {
      this.fetchPage({ replace: true });
    }
    this.ensureScrollListener();
  }

  ensureScrollListener() {
    if (this._scrollListenerAttached) {
      return;
    }
    const container = this.element.closest('.choices') || (
      this.choices && this.choices.containerOuter && this.choices.containerOuter.element
    );
    if (!container) {
      return;
    }
    // Try inner scrolling list first, then fallback to dropdown container
    const innerList = container.querySelector('.choices__list--dropdown .choices__list');
    const dropdownContainer = container.querySelector('.choices__list--dropdown');
    const scrollingNode = innerList || dropdownContainer;
    if (!scrollingNode) {
      return;
    }
    scrollingNode.addEventListener('scroll', () => {
      if (this.isLoading || this.reachedLastPage) {
        return;
      }
      const threshold = 50; // px from bottom to trigger next page
      const distanceFromBottom = scrollingNode.scrollHeight - scrollingNode.scrollTop - scrollingNode.clientHeight;
      if (distanceFromBottom <= threshold) {
        this.currentPage += 1;
        this.fetchPage({ replace: false });
      }
    });
    this._scrollListenerAttached = true;
  }

  buildUrl(page) {
    const url = new URL(this.urlValue, window.location.href);
    if (this.lastQuery !== undefined) {
      url.searchParams.append(`q[${this.queryNameValue}]`, this.lastQuery);
    }
    if (this.additionalQueryParamsValue != null) {
      Object.entries(JSON.parse(this.additionalQueryParamsValue)).forEach(([key, value]) => {
        if (value !== null) {
          if (Array.isArray(value)) {
            value.forEach(val => url.searchParams.append(`q[${key}][]`, val));
          } else {
            url.searchParams.append(`q[${key}]`, value);
          }
        }
      });
    }
    if (page != null) {
      url.searchParams.append('page', String(page));
    }
    return url;
  }

  fetchPage({ replace }) {
    this.isLoading = true;
    const url = this.buildUrl(this.currentPage);
    const labelField = this.labelFieldValue || 'label';
    fetch(url)
      .then(response => response.json())
      .then(items => {
        let selectedValues = this.choices ? this.choices.getValue(true) : [];
        if (!Array.isArray(selectedValues)) {
          selectedValues = selectedValues ? [selectedValues] : [];
        }
        selectedValues = selectedValues.map(v => String(v));
        const filteredItems = (items || []).filter(
          item => !selectedValues.includes(String(item.id)) && !selectedValues.includes(String(item.value))
        );

        if (replace) {
          this.choices.setChoices(filteredItems, 'id', labelField, true);
        } else if (filteredItems.length > 0) {
          this.choices.setChoices(filteredItems, 'id', labelField, false);
        }
        if (!items || items.length === 0) {
          this.reachedLastPage = true;
        }
        // After rendering choices, ensure scroll listener is attached
        this.ensureScrollListener();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }
}
